/*
発注エクセル取り込み/出力に使用する型情報をまとめる
*/

import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { i18n } from '@/i18n';
import { h, VNode } from 'vue';

/**
 * 発注エクセルの取り込み/出力用の発注登録用の取引情報の型
 */
export type TranslateExcelOrderBusiness = {
  translateOrderBusinessId: string; // 発注エクセル取り込みのみに用いるID
  warehouseNumber: string; // 置場CD
  warehouse: string; // 置場名
  package: string; // 容量
  businessNumber: string; // 商品CD
  businessName: string; // 商品名
  supplierNumber: string; // 仕入先CD
  supplierName: string; // 仕入先名
  excelOrders: { [key: string]: TranslateExcelOrder }; // 発注情報の型
};

/**
 * 発注エクセルの取り込み/出力用の発注の型
 * 日付情報はvalidationチェックのために一旦文字列で取り扱う
 */
export type TranslateExcelOrder = {
  crossDate: string; // 納入日
  amount: number; // 数量
  fixOrderDate: string; // 確定発注日
  orderIssuedNumber?: string;
  orderNumber: string; // 発注番号
  status: OrderStatus; // 発注ステータス
  excelInfo: OrderBusinessExcelInfo; // エクセル情報
};

/**
 * エクセル読み取り/出力の発注情報を構成するために必須の情報についての型定義
 * TranslateExcelOrderまたは最低限TranslateExcelOrderIdに合致する情報が必須であることを示す
 */
export type RequiredTranslateExcelOrder = TranslateExcelOrderBusiness | Partial<TranslateExcelOrderBusinessId>;

/**
 * 対象の読み取り/出力発注情報が有効な集計区別用のIDを構成するための型
 * 発注のユニーク条件は「置場CD、商品CD、仕入先CD、容量、納入日、(数量)」
 * 数量は登録済みの発注との比較の際に必要になる条件なので、フロントでは取り扱わない(バックエンドで見る)
 */
type TranslateExcelOrderBusinessId = {
  warehouseNumber: string; // 倉庫CD
  package: string; // 容量
  businessNumber: string; // 商品CD
  supplierNumber: string; // 仕入先CD
};

/**
 * 発注エクセルの読取情報
 * セル番号、シート名、ファイル名を保持する
 */
type OrderBusinessExcelInfo = {
  cellNumber: string | number | undefined; // セル番号
  sheetName: string | number | undefined; // シート名
  fileName: string; // ファイル名
};

/**
 * 対象の読み取り/出力発注情報が有効な集計区別用のID作成に必要なパラメータを有しているか(欠けがないか)を確認する
 * @param translateOrder - 取引情報
 */
export const isValidTranslateExcelOrderId = (
  translateOrder: RequiredTranslateExcelOrder
): translateOrder is RequiredTranslateExcelOrder => {
  return (
    Object.hasOwn(translateOrder, 'warehouseNumber') &&
    Object.hasOwn(translateOrder, 'package') &&
    Object.hasOwn(translateOrder, 'businessNumber') &&
    Object.hasOwn(translateOrder, 'supplierNumber') &&
    Object.hasOwn(translateOrder, 'excelOrders')
  );
};

/**
 * Excel読み込み/整形のリザルトタイプ
 */
export type TranslateExcelOrderBusinessResult = {
  splitOrderExcelData: Record<string, TranslateExcelOrderBusiness>;
  isSuccess: boolean;
};

/**
 * 発注取引の登録処理結果を集計した数値情報
 */
export type RegisterOrderBusinessResultCounts = {
  registered: {
    updated: number;
    linkedOrderNumberByIssued: number;
    linkedOrderNumberByEtaAmount: number;
    unmatched: number;
  };
  alerted: {
    orderWithoutNumber: number;
    outOfStockOrder: number;
    overdueOrder: number;
  };
};

/**
 * アラートが立っている発注が1件以上あるかどうか
 * @param countData - 集計数値情報
 */
export const hasAlertCount = (countData: RegisterOrderBusinessResultCounts) =>
  countData.alerted && Object.values(countData.alerted).some((v) => Number.isInteger(v) && v > 0);

/**
 * 発注登録完了モーダルのContentを返す
 * @param countData - 登録件数情報
 */
export const showOrderImportSuccessModalContent = (countData: { data: number; title: string; isError?: boolean; link?: string }[]) => {
  const t = i18n.t;
  return h(
    'div',
    { style: { display: 'grid', gridTemplateColumns: `repeat(${countData.length}, 1fr)`, fontFamily: 'Noto Sans JP' } },
    countData.map((item, index) => {
      let valueNode: VNode = h('div', { style: { fontWeight: '700', color: item.isError ? '#d50000' : 'inherit' } }, [
        h('span', { style: { fontSize: '32px' } }, item.data),
        h('span', { style: { fontSize: '24px' } }, ` ${t('件')}`),
      ]);
      if (item.link) {
        valueNode = h(
          'a',
          { href: `#${item.link}`, target: '_blank', style: { color: '#1e92ef', borderBottom: '1px solid #1e92ef' } },
          valueNode
        );
      }
      return h(
        'div',
        {
          style: {
            display: 'grid',
            gridTemplateRows: 'repeat(2, 1fr)',
            gap: '8px',
            justifyContent: 'center',
            justifyItems: 'center',
          },
          key: index,
        },
        [h('div', { style: { fontSize: '16px' } }, t(item.title)), valueNode]
      );
    })
  );
};
